<script setup lang="ts">
  import type { MenuOption } from 'naive-ui'
  import { NIcon, useDialog, useMessage } from 'naive-ui'
  import { Dashboard, Logout, FileAnalytics, Settings, News, Users } from '@vicons/tabler'

  type AdminUser = {
    id: number
    username: string
    role: string
    displayName: string | null
    gender?: number
  }

  const route = useRoute()
  const localePath = useLocalePath()
  const dialog = useDialog()
  const message = useMessage()
  const collapsed = defineModel<boolean>('collapsed', { default: false })

  const { data: meData } = await useFetch<{ ok: boolean; user: AdminUser }>('/api/auth/me')

  const user = computed(() => meData.value?.user ?? null)

  const displayName = computed(() => user.value?.displayName || user.value?.username || '未登录')
  const roleLabel = computed(() => getAdminRoleLabel(user.value?.role))

  const avatarSrc = computed(() => getAdminUserAvatarSrc(user.value?.gender))

  function renderIcon(icon: Component) {
    return () => h(NIcon, null, { default: () => h(icon) })
  }

  /** Strip optional i18n locale prefix: /en/admin/... -> /admin/... */
  function normalizeAdminPath(path: string) {
    return path.replace(/^\/[a-z]{2}(?=\/)/, '')
  }

  const menuOptions = computed<MenuOption[]>(() => [
    {
      label: 'Welcome',
      key: 'dashboard',
      icon: renderIcon(Dashboard),
    },
    {
      label: '内容管理',
      key: 'content',
      icon: renderIcon(FileAnalytics),
      children: [
        {
          label: '新闻管理',
          key: 'news',
          icon: renderIcon(News),
        },
      ],
    },
    {
      label: '系统管理',
      key: 'system',
      icon: renderIcon(Settings),
      children: [
        {
          label: '管理员',
          key: 'user',
          icon: renderIcon(Users),
        },
      ],
    },
  ])

  const routeKeyMap: Record<string, string> = {
    dashboard: '/admin/dashboard',
    news: '/admin/news',
    user: '/admin/user',
  }

  const activeKey = computed(() => {
    const path = normalizeAdminPath(route.path)
    if (path.startsWith('/admin/news')) return 'news'
    if (path.startsWith('/admin/user')) return 'user'
    if (path.startsWith('/admin/dashboard') || path === '/admin' || path === '/admin/') {
      return 'dashboard'
    }
    return null
  })

  const expandedKeys = ref<string[]>(['content', 'system'])

  function onSelect(key: string) {
    const path = routeKeyMap[key]
    if (!path) return
    navigateTo(localePath(path))
  }

  function onExpandedKeysUpdate(keys: string[]) {
    expandedKeys.value = keys
  }

  async function doLogout() {
    try {
      await $fetch('/api/auth/logout', { method: 'POST' })
      message.success('已退出登录')
      await navigateTo(localePath('/admin/login'))
    } catch {
      message.error('退出失败，请重试')
    }
  }

  function onLogout() {
    dialog.create({
      title: '退出登录',
      content: '确定退出当前账号吗？',
      positiveText: '退出',
      negativeText: '取消',
      onPositiveClick: doLogout,
    })
  }
</script>

<template>
  <aside class="admin-sidebar" :class="{ 'admin-sidebar--collapsed': collapsed }">
    <div class="admin-sidebar__brand p-5 pl-4 flex items-center gap-2">
      <div v-if="!collapsed" class="flex items-center gap-2">
        <n-image width="30" src="/images/logo.png" />
        <div class="leading-8.75 h-7.5 font-bold">芯位人工智能学院</div>
      </div>
      <div v-else>
        <n-image width="30" src="/images/logo.png" />
      </div>
    </div>

    <n-scrollbar class="admin-sidebar__scroll">
      <n-menu
        :value="activeKey"
        :options="menuOptions"
        :collapsed="collapsed"
        :collapsed-width="64"
        :collapsed-icon-size="22"
        :expanded-keys="expandedKeys"
        :indent="18"
        @update:value="onSelect"
        @update:expanded-keys="onExpandedKeysUpdate"
      />
    </n-scrollbar>

    <div class="admin-sidebar__footer" :class="{ 'admin-sidebar__footer--collapsed': collapsed }">
      <div class="admin-sidebar__user">
        <n-avatar round :size="40" :src="avatarSrc" />
        <div v-if="!collapsed" class="admin-sidebar__meta">
          <div class="admin-sidebar__name" :title="displayName">{{ displayName }}</div>
          <div class="admin-sidebar__role">{{ roleLabel }}</div>
        </div>
      </div>

      <n-tooltip v-if="collapsed" placement="right">
        <template #trigger>
          <button type="button" class="admin-sidebar__logout" aria-label="退出登录" @click="onLogout">
            <NIcon :component="Logout" :size="20" />
          </button>
        </template>
        退出登录
      </n-tooltip>
      <button v-else type="button" class="admin-sidebar__logout" aria-label="退出登录" @click="onLogout">
        <NIcon :component="Logout" :size="20" />
      </button>
    </div>
  </aside>
</template>

<style lang="scss" scoped>
  .admin-sidebar {
    display: flex;
    flex-direction: column;
    width: 270px;
    height: calc(100vh - 40px);
    background: #fff;
    border-radius: 18px;
    position: fixed;
    top: 20px;
    left: 20px;
    overflow: hidden;
  }

  .admin-sidebar--collapsed {
    width: 64px;
  }

  .admin-sidebar__brand {
    flex-shrink: 0;
  }

  .admin-sidebar__scroll {
    flex: 1;
    min-height: 0;
  }

  .admin-sidebar__footer {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 14px;
    background: #e5f3fb;
    margin: 16px;
    border-radius: 10px;
  }

  .admin-sidebar__footer--collapsed {
    flex-direction: column;
    justify-content: center;
    padding: 12px 8px;
  }

  .admin-sidebar__user {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .admin-sidebar__meta {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .admin-sidebar__name {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .admin-sidebar__role {
    margin-top: 2px;
    font-size: 12px;
    color: #8b93a7;
    line-height: 1.3;
  }

  .admin-sidebar__logout {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
  }
</style>
